import React, { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL, formatApiError } from "../../lib/api";
import { useCompany } from "../../contexts/CompanyContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../../components/ui/accordion";
import { BottomSheet } from "../../components/ui/bottom-sheet";
import MapPicker from "../../components/MapPicker";
import ServiceAreaEditor from "../../components/ServiceAreaEditor";
import ImageCropDialog from "../../components/ImageCropDialog";
import { toast } from "sonner";
import { useConfirm } from "../../components/ConfirmProvider";
import {
  Upload, ImageIcon, Building2, Share2, MapPin, Satellite, Fuel, Truck,
  Instagram, Facebook, Phone, MessageCircle, RefreshCw, CheckCircle2,
  KeyRound, Globe, Tag, RotateCcw, Pencil, Plus, Trash2, Clock, SlidersHorizontal,
  Banknote,
} from "lucide-react";
import GasCylinderIcon from "../../components/icons/GasCylinderIcon";
import HeroImagePicker from "../../components/HeroImagePicker";
import PaymentLogo from "../../components/icons/PaymentLogo";
import { PAYMENT_METHODS, DEFAULT_ENABLED_METHODS } from "../../lib/paymentMethods";

/**
 * Reloj en vivo que muestra fecha + hora completas en la zona horaria
 * seleccionada. Se actualiza cada segundo. Si `tz` es null/vacío, usa
 * UTC (el default del backend).
 */
function TzClock({ tz }) {
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const zone = tz || "UTC";
  let text = "";
  try {
    text = new Intl.DateTimeFormat("es-MX", {
      timeZone: zone,
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(now);
  } catch {
    text = now.toISOString();
  }
  return (
    <span
      className="ml-2 inline-flex items-center gap-1 text-[11px] font-mono tabular-nums font-normal text-neutral-500"
      data-testid="tz-clock"
      title={`Hora actual en ${zone}`}
    >
      <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
      ({text})
    </span>
  );
}

export default function AdminBranding() {
  const { setCurrentCompany, currentCompany } = useCompany();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("general");
  const [uploading, setUploading] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { api.get("/companies/mine").then((r) => setC(r.data)); }, []);

  // Map each tab to the subset of fields it should PATCH. Saving from one
  // tab no longer overwrites unsaved drafts in another tab — the diff sent
  // to the server is scoped to whatever the user is actually editing.
  const PAYLOAD_FOR_TAB = {
    general: () => ({
      name: c.name,
      primary_color: c.primary_color,
      logo_url: c.logo_url,
      description: c.description,
    }),
    config: () => ({
      min_order_amount: c.min_order_amount === null || c.min_order_amount === ""
        ? null : Math.max(0, Number(c.min_order_amount) || 0),
      timezone: c.timezone || null,
      hero_image_bulk: c.hero_image_bulk || null,
      hero_image_cylinder: c.hero_image_cylinder || null,
      hero_image_station: c.hero_image_station || null,
      hero_default_bulk: !!c.hero_default_bulk,
      hero_default_cylinder: !!c.hero_default_cylinder,
      hero_default_station: !!c.hero_default_station,
      payment_methods: Array.isArray(c.payment_methods)
        ? c.payment_methods
        : null,
      ai_mode: !!c.ai_mode,
    }),
    horarios: () => ({
      business_hours: c.business_hours || null,
      close_mode: c.close_mode || (c.temporarily_closed ? "today" : "none"),
      temporarily_closed: !!c.temporarily_closed,
      eta_tolerance_min: c.eta_tolerance_min === "" || c.eta_tolerance_min === null || c.eta_tolerance_min === undefined
        ? null
        : Math.max(0, parseInt(c.eta_tolerance_min, 10) || 0),
    }),
    redes: () => ({
      phone: c.phone,
      whatsapp: c.whatsapp,
      instagram: c.instagram,
      facebook: c.facebook,
    }),
    ubicaciones: () => ({
      supply_address: c.supply_address,
      supply_lat: c.supply_lat,
      supply_lng: c.supply_lng,
      service_area_polygons: Array.isArray(c.service_area_polygons) ? c.service_area_polygons : [],
    }),
    pipas: () => ({
      samsara_api_token: c.samsara_api_token,
      samsara_region: c.samsara_region,
      samsara_group_id: c.samsara_group_id,
    }),
    cilindreras: () => ({
      samsara_cil_api_token: c.samsara_cil_api_token,
      samsara_cil_region: c.samsara_cil_region,
      samsara_cil_group_id: c.samsara_cil_group_id,
    }),
    estaciones: () => ({
      // The estaciones tab is a sub-CRUD over /api/stations; nothing on the
      // company doc itself. Saving it is a no-op so the button can stay
      // hidden via SAVE_DISABLED below.
    }),
  };

  const SAVE_LABEL = {
    general: "Guardar General",
    config: "Guardar Configuración",
    horarios: "Guardar Horarios",
    redes: "Guardar Redes",
    ubicaciones: "Guardar Ubicaciones",
    pipas: "Guardar Autotanques",
    cilindreras: "Guardar Cilindreras",
    estaciones: "Estaciones",
  };
  const SAVE_DISABLED = { estaciones: true };

  const save = async () => {
    const buildPayload = PAYLOAD_FOR_TAB[tab];
    if (!buildPayload) return;
    const payload = buildPayload();
    if (!payload || Object.keys(payload).length === 0) return;
    if (
      tab === "ubicaciones" &&
      (!Array.isArray(payload.service_area_polygons) || payload.service_area_polygons.length === 0)
    ) {
      toast.error("Dibuja al menos un área de cobertura antes de guardar.");
      return;
    }
    try {
      const { data } = await api.patch(`/companies/${c.company_id}`, payload);
      setC(data);
      // Live-update the TopBar/global brand if this is the current company
      if (currentCompany?.company_id === data.company_id) {
        setCurrentCompany(data);
      }
      toast.success("Cambios guardados");
      if ("primary_color" in payload && data.primary_color) {
        document.documentElement.style.setProperty("--company-primary", data.primary_color);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("La imagen supera 3MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCropImageSrc(ev.target?.result);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const confirmCroppedUpload = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "logo.png");
      const { data } = await api.post("/uploads/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${BACKEND_URL}${data.url}`;
      setC((prev) => ({ ...prev, logo_url: fullUrl }));
      toast.success("Logo subido. Guarda los cambios para aplicarlo.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error subiendo imagen");
    } finally {
      setUploading(false);
    }
  };

  const onSupplyPin = (pin) => {
    if (!pin) return;
    setC((prev) => ({
      ...prev,
      supply_address: pin.full_address ?? prev.supply_address,
      supply_lat: pin.lat ?? prev.supply_lat,
      supply_lng: pin.lng ?? prev.supply_lng,
    }));
  };

  if (!c) return null;

  const supplyValue = (c.supply_lat && c.supply_lng)
    ? { lat: c.supply_lat, lng: c.supply_lng, full_address: c.supply_address || "" }
    : null;

  return (
    <div className="min-h-screen bg-neutral-50">
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Empresa</h1>
        <p className="text-sm text-neutral-500 mt-1">Personaliza la experiencia de tus clientes.</p>

        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <TabsList className="grid w-full grid-cols-4 md:grid-cols-8 h-auto md:h-12 bg-neutral-100 p-1 rounded-xl gap-1">
            <TabsTrigger value="general" className="data-[state=active]:bg-white rounded-lg gap-1.5" data-testid="tab-general">
              <Building2 size={16} className="shrink-0" /><span className="hidden sm:inline">General</span>
            </TabsTrigger>
            <TabsTrigger value="config" className="data-[state=active]:bg-white rounded-lg gap-1.5" data-testid="tab-config">
              <SlidersHorizontal size={16} className="shrink-0" /><span className="hidden sm:inline">Configuración</span>
            </TabsTrigger>
            <TabsTrigger value="horarios" className="data-[state=active]:bg-white rounded-lg gap-1.5" data-testid="tab-horarios">
              <Clock size={16} className="shrink-0" /><span className="hidden sm:inline">Horarios</span>
            </TabsTrigger>
            <TabsTrigger value="redes" className="data-[state=active]:bg-white rounded-lg gap-1.5" data-testid="tab-redes">
              <Share2 size={16} className="shrink-0" /><span className="hidden sm:inline">Redes</span>
            </TabsTrigger>
            <TabsTrigger value="ubicaciones" className="data-[state=active]:bg-white rounded-lg gap-1.5" data-testid="tab-ubicaciones">
              <MapPin size={16} className="shrink-0" /><span className="hidden sm:inline">Ubicaciones</span>
            </TabsTrigger>
            <TabsTrigger value="pipas" className="data-[state=active]:bg-white rounded-lg gap-1.5" data-testid="tab-pipas">
              <Truck size={16} className="shrink-0" /><span className="hidden sm:inline">Autotanques</span>
            </TabsTrigger>
            <TabsTrigger value="cilindreras" className="data-[state=active]:bg-white rounded-lg gap-1.5" data-testid="tab-cilindreras">
              <GasCylinderIcon size={16} className="shrink-0" /><span className="hidden sm:inline">Cilindreras</span>
            </TabsTrigger>
            <TabsTrigger value="estaciones" className="data-[state=active]:bg-white rounded-lg gap-1.5" data-testid="tab-estaciones">
              <Fuel size={16} className="shrink-0" /><span className="hidden sm:inline">Estaciones</span>
            </TabsTrigger>
          </TabsList>

          {/* GENERAL */}
          <TabsContent value="general" className="mt-4">
            <div className="bg-white rounded-2xl border border-neutral-200 p-6 space-y-5">
              <div>
                <Label>Nombre de la empresa</Label>
                <Input className="h-11" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} data-testid="brand-name" />
              </div>

              <div>
                <Label>Color primario</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={c.primary_color || "#0A0A0A"}
                    onChange={(e) => setC({ ...c, primary_color: e.target.value })}
                    className="h-11 w-14 rounded-lg border border-neutral-200 cursor-pointer"
                    data-testid="brand-color-picker"
                  />
                  <Input className="h-11" value={c.primary_color} onChange={(e) => setC({ ...c, primary_color: e.target.value })} data-testid="brand-color-hex" />
                </div>
              </div>

              <div>
                <Label>Logo</Label>
                <div className="flex items-center gap-4">
                  <div className="h-20 w-20 rounded-full border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden shrink-0">
                    {c.logo_url ? (
                      <img src={c.logo_url} alt="logo" className="h-full w-full object-cover" data-testid="logo-preview" />
                    ) : (
                      <ImageIcon size={24} className="text-neutral-400" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="logo-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="h-10"
                      data-testid="upload-logo-btn"
                    >
                      <Upload size={14} className="mr-2" />
                      {uploading ? "Subiendo..." : "Subir y recortar imagen"}
                    </Button>
                    <Input
                      className="h-10 text-xs bg-neutral-50"
                      placeholder="URL del logo (generada automáticamente)"
                      value={c.logo_url || ""}
                      readOnly
                      disabled
                      data-testid="brand-logo-url"
                    />
                    <p className="text-xs text-neutral-400">PNG, JPG, WEBP o GIF. Máx 3MB. Se recorta en círculo.</p>
                  </div>
                </div>
              </div>

              <div>
                <Label>Descripción</Label>
                <Textarea value={c.description || ""} onChange={(e) => setC({ ...c, description: e.target.value })} data-testid="brand-desc" placeholder="¿En qué se diferencia tu empresa?" />
              </div>
            </div>
          </TabsContent>

          {/* CONFIGURACIÓN */}
          <TabsContent value="config" className="mt-4">
            <div className="bg-white rounded-2xl border border-neutral-200 p-6 space-y-5">
              <p className="text-sm text-neutral-500">
                Reglas operativas que afectan cómo se aceptan y procesan los pedidos.
              </p>

              {/* Zona horaria */}
              <div className="rounded-xl border border-neutral-200 p-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label htmlFor="company-timezone" className="text-sm font-semibold">
                    Zona horaria
                    <TzClock tz={c.timezone} />
                  </Label>
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  Define el "día" de tu empresa. Afecta los KPIs del panel de control
                  ("Ingresos hoy", "Ayer", "Pedidos hoy") y el embudo diario.
                </p>
                <Select
                  value={c.timezone || "UTC"}
                  onValueChange={(v) => setC({ ...c, timezone: v === "UTC" ? null : v })}
                >
                  <SelectTrigger className="h-11 mt-3" data-testid="company-timezone">
                    <SelectValue placeholder="Selecciona una zona" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTC">UTC (por defecto)</SelectItem>
                    <SelectItem value="America/Mexico_City">América / Ciudad de México (CDMX)</SelectItem>
                    <SelectItem value="America/Monterrey">América / Monterrey</SelectItem>
                    <SelectItem value="America/Tijuana">América / Tijuana</SelectItem>
                    <SelectItem value="America/Cancun">América / Cancún</SelectItem>
                    <SelectItem value="America/Hermosillo">América / Hermosillo</SelectItem>
                    <SelectItem value="America/Bogota">América / Bogotá</SelectItem>
                    <SelectItem value="America/Lima">América / Lima</SelectItem>
                    <SelectItem value="America/Santiago">América / Santiago</SelectItem>
                    <SelectItem value="America/Argentina/Buenos_Aires">América / Buenos Aires</SelectItem>
                    <SelectItem value="America/Caracas">América / Caracas</SelectItem>
                    <SelectItem value="America/Guatemala">América / Guatemala</SelectItem>
                    <SelectItem value="America/Tegucigalpa">América / Tegucigalpa</SelectItem>
                    <SelectItem value="America/Managua">América / Managua</SelectItem>
                    <SelectItem value="America/Costa_Rica">América / Costa Rica</SelectItem>
                    <SelectItem value="America/Panama">América / Panamá</SelectItem>
                    <SelectItem value="America/Santo_Domingo">América / Santo Domingo</SelectItem>
                    <SelectItem value="America/New_York">América / Nueva York</SelectItem>
                    <SelectItem value="America/Los_Angeles">América / Los Ángeles</SelectItem>
                    <SelectItem value="Europe/Madrid">Europa / Madrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Minimum order amount per company — applies to the total of any cart */}
              <div className="rounded-xl border border-[color:var(--company-primary)]/30 p-4 bg-company/5">
                <div className="flex items-center gap-2">
                  <input
                    id="company-min-enabled"
                    type="checkbox"
                    checked={c.min_order_amount !== null && c.min_order_amount !== undefined}
                    onChange={(e) => setC({ ...c, min_order_amount: e.target.checked ? 100 : null })}
                    className="h-4 w-4 accent-[color:var(--company-primary)]"
                    data-testid="company-min-enabled"
                  />
                  <label htmlFor="company-min-enabled" className="text-sm font-semibold">
                    Venta mínima del pedido
                  </label>
                </div>
                {c.min_order_amount !== null && c.min_order_amount !== undefined ? (
                  <div className="mt-2">
                    <Label className="text-xs text-neutral-500">Monto mínimo ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="100"
                      className="h-11 mt-1"
                      value={c.min_order_amount}
                      onChange={(e) => setC({ ...c, min_order_amount: e.target.value })}
                      data-testid="company-min-amount"
                    />
                    <p className="text-[11px] text-neutral-500 mt-1">
                      Cualquier pedido del cliente deberá sumar al menos este monto total (sin importar los productos). Aumenta de $100 en $100.
                    </p>
                  </div>
                ) : (
                  <p className="text-[11px] text-neutral-500 mt-1">
                    Activa esta opción para exigir un monto mínimo en cada pedido (ej. $300).
                  </p>
                )}
              </div>

              {/* Métodos de pago — checkboxes para controlar qué métodos
                  ve el cliente en el wizard. Si todos están deshabilitados,
                  el cliente verá un aviso de "contactar al admin". */}
              <PaymentMethodsConfig
                selected={c.payment_methods}
                onChange={(next) => setC({ ...c, payment_methods: next })}
              />

              {/* ==== Modo AI ==== */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-gradient-to-br from-indigo-50/40 to-white">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-indigo-600 text-white text-xs font-bold">AI</span>
                      <Label className="text-sm font-semibold">Modo AI · Auto-piloto</Label>
                      {c.ai_mode ? (
                        <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="ai-mode-status-active">Activo</span>
                      ) : (
                        <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-neutral-100 text-neutral-600 border border-neutral-200" data-testid="ai-mode-status-inactive">Manual</span>
                      )}
                    </div>
                    <p className="text-xs text-neutral-600 mt-1.5 leading-relaxed">
                      Cuando está activo, cada nuevo pedido se <strong>confirma</strong> y se le <strong>asigna una unidad disponible</strong> automáticamente, sin intervención del operador. Cada acción queda registrada en la línea de tiempo del pedido como "Auto-piloto AI".
                    </p>
                    <p className="text-[11px] text-neutral-500 mt-1">
                      Si no hay unidades disponibles, el pedido queda en <em>confirmado</em> esperando asignación manual.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!c.ai_mode}
                    onClick={() => setC({ ...c, ai_mode: !c.ai_mode })}
                    className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${c.ai_mode ? "bg-indigo-600" : "bg-neutral-300"}`}
                    data-testid="ai-mode-toggle"
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${c.ai_mode ? "translate-x-6" : "translate-x-1"}`}
                    />
                  </button>
                </div>
              </div>

              {/* Hero images — what the client sees on Home. */}
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-semibold">Imágenes de inicio</Label>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    Imagen que verá el cliente en los 3 botones principales de "¿Qué necesitas hoy?". Si no subes una, se mostrará un icono por defecto con tu color de marca.
                  </p>
                </div>
                <HeroImagePicker
                  value={c.hero_image_bulk}
                  defaultMode={!!c.hero_default_bulk}
                  onChange={(url, useDefault) => setC({ ...c, hero_image_bulk: url, hero_default_bulk: !!useDefault })}
                  kind="bulk"
                  label="Autotanque (Gas a granel)"
                  description="Icono por defecto: 🔥 flama"
                  testId="hero-image-bulk"
                />
                <HeroImagePicker
                  value={c.hero_image_cylinder}
                  defaultMode={!!c.hero_default_cylinder}
                  onChange={(url, useDefault) => setC({ ...c, hero_image_cylinder: url, hero_default_cylinder: !!useDefault })}
                  kind="cylinder"
                  label="Cilindreras"
                  description="Icono por defecto: cilindro"
                  testId="hero-image-cylinder"
                />
                <HeroImagePicker
                  value={c.hero_image_station}
                  defaultMode={!!c.hero_default_station}
                  onChange={(url, useDefault) => setC({ ...c, hero_image_station: url, hero_default_station: !!useDefault })}
                  kind="station"
                  label="Estaciones"
                  description="Icono por defecto: pin de ubicación"
                  testId="hero-image-station"
                />
              </div>
            </div>
          </TabsContent>

          {/* HORARIOS */}
          <TabsContent value="horarios" className="mt-4">
            <div className="bg-white rounded-2xl border border-neutral-200 p-6 space-y-5">
              <p className="text-sm text-neutral-500">
                Define los horarios de atención. Cuando estés cerrado, los clientes sólo podrán programar pedidos para días abiertos.
              </p>

              {/* Close-mode: 3-state kill-switch */}
              <div className="space-y-2">
                <p className="text-sm font-semibold">Estado de la tienda</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  {[
                    { v: "none", title: "Abierta", desc: "Las ventas funcionan según el horario semanal.", tone: "emerald" },
                    { v: "today", title: "Cerrar hoy", desc: "Cierra lo que queda del día. Los clientes pueden programar para mañana u otro día abierto.", tone: "amber" },
                    { v: "permanent", title: "Cerrar permanentemente", desc: "Pausa total de ventas. No se aceptan pedidos inmediatos ni programados.", tone: "red" },
                  ].map((opt) => {
                    const selected = (c.close_mode || "none") === opt.v;
                    const toneActive = opt.tone === "amber"
                      ? "border-amber-400 bg-amber-50"
                      : opt.tone === "red"
                        ? "border-red-400 bg-red-50"
                        : "border-emerald-400 bg-emerald-50";
                    return (
                      <label
                        key={opt.v}
                        className={`cursor-pointer rounded-2xl border-2 p-4 transition ${selected ? toneActive : "border-neutral-200 bg-white hover:border-neutral-300"}`}
                        data-testid={`close-mode-${opt.v}`}
                      >
                        <input
                          type="radio"
                          name="close_mode"
                          className="sr-only"
                          checked={selected}
                          onChange={() => setC({ ...c, close_mode: opt.v, temporarily_closed: opt.v === "today" })}
                        />
                        <p className={`font-semibold text-sm ${
                          selected
                            ? (opt.tone === "amber" ? "text-amber-900" : opt.tone === "red" ? "text-red-900" : "text-emerald-900")
                            : "text-neutral-900"
                        }`}>
                          {opt.title}
                        </p>
                        <p className="text-xs text-neutral-600 mt-1 leading-snug">{opt.desc}</p>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Weekly schedule */}
              <div className="rounded-xl border border-neutral-200 divide-y" data-testid="business-hours">
                {[
                  { k: "mon", t: "Lunes" },
                  { k: "tue", t: "Martes" },
                  { k: "wed", t: "Miércoles" },
                  { k: "thu", t: "Jueves" },
                  { k: "fri", t: "Viernes" },
                  { k: "sat", t: "Sábado" },
                  { k: "sun", t: "Domingo" },
                ].map(({ k, t }) => {
                  const hours = c.business_hours || {};
                  const d = hours[k] || { closed: false, open: "08:00", close: "20:00" };
                  const update = (patch) => setC({ ...c, business_hours: { ...hours, [k]: { ...d, ...patch } } });
                  return (
                    <div
                      key={k}
                      className={`grid grid-cols-[110px_auto_1fr_auto_1fr] items-center gap-3 px-4 py-3 transition-colors ${
                        d.closed
                          ? "bg-neutral-50/40"
                          : "bg-company/5 border-l-2 border-company"
                      }`}
                    >
                      <span className={`font-semibold text-sm ${d.closed ? "text-neutral-500" : "text-[color:var(--company-primary)]"}`}>{t}</span>
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={!d.closed}
                          onChange={(e) => update({ closed: !e.target.checked })}
                          className="h-4 w-4 accent-[color:var(--company-primary)]"
                          data-testid={`hours-open-${k}`}
                        />
                        Abierto
                      </label>
                      <div className="flex items-center gap-2 col-span-3">
                        <Input
                          type="time"
                          className="h-9 w-28"
                          disabled={d.closed}
                          value={d.open || ""}
                          onChange={(e) => update({ open: e.target.value })}
                          data-testid={`hours-open-time-${k}`}
                        />
                        <span className="text-xs text-neutral-400">a</span>
                        <Input
                          type="time"
                          className="h-9 w-28"
                          disabled={d.closed}
                          value={d.close || ""}
                          onChange={(e) => update({ close: e.target.value })}
                          data-testid={`hours-close-time-${k}`}
                        />
                        {d.closed && <span className="text-xs text-neutral-500">Cerrado</span>}
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="text-[11px] text-neutral-500">
                Si no configuras horarios, la tienda aparecerá siempre abierta para los clientes.
              </p>

              {/* ETA tolerance — added to the raw Google Distance Matrix
                  ETA before showing it to the customer. Vinculado a horarios
                  porque es un buffer operativo. */}
              <div className="rounded-2xl border border-neutral-200 p-5">
                <Label htmlFor="eta-tolerance" className="text-sm font-semibold">Tolerancia de tiempo (min)</Label>
                <p className="text-xs text-neutral-500 mt-1">
                  Minutos extra que se suman al tiempo estimado de llegada (ETA) antes de mostrarlo al cliente. Ideal para reflejar tiempos de carga, tráfico o margen operativo.
                </p>
                <div className="flex items-center gap-3 mt-3">
                  <Input
                    id="eta-tolerance"
                    type="number"
                    min="0" max="120" step="1"
                    placeholder="0"
                    className="h-10 w-28"
                    value={c.eta_tolerance_min ?? ""}
                    onChange={(e) => setC({ ...c, eta_tolerance_min: e.target.value === "" ? null : Math.max(0, parseInt(e.target.value, 10) || 0) })}
                    data-testid="eta-tolerance-input"
                  />
                  <span className="text-xs text-neutral-500">minutos</span>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* REDES SOCIALES */}
          <TabsContent value="redes" className="mt-4">
            <div className="bg-white rounded-2xl border border-neutral-200 p-6 space-y-5">
              <p className="text-sm text-neutral-500">Datos de contacto y redes que verán tus clientes en tu perfil.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2"><Phone size={13} className="text-neutral-500" />Teléfono</Label>
                  <Input className="h-11" value={c.phone || ""} onChange={(e) => setC({ ...c, phone: e.target.value })} placeholder="+57 300 000 0000" data-testid="brand-phone" />
                </div>
                <div>
                  <Label className="flex items-center gap-2"><MessageCircle size={13} className="text-neutral-500" />WhatsApp</Label>
                  <Input className="h-11" value={c.whatsapp || ""} onChange={(e) => setC({ ...c, whatsapp: e.target.value })} placeholder="+57 300 000 0000" data-testid="brand-whatsapp" />
                </div>
                <div>
                  <Label className="flex items-center gap-2"><Instagram size={13} className="text-neutral-500" />Instagram</Label>
                  <Input className="h-11" value={c.instagram || ""} onChange={(e) => setC({ ...c, instagram: e.target.value })} placeholder="@tuempresa" data-testid="brand-instagram" />
                </div>
                <div>
                  <Label className="flex items-center gap-2"><Facebook size={13} className="text-neutral-500" />Facebook</Label>
                  <Input className="h-11" value={c.facebook || ""} onChange={(e) => setC({ ...c, facebook: e.target.value })} placeholder="tuempresa" data-testid="brand-facebook" />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* UBICACIONES */}
          <TabsContent value="ubicaciones" className="mt-4">
            <div className="bg-white rounded-2xl border border-neutral-200 p-6 space-y-5">
              <div>
                <Label>Ubicación de la empresa</Label>
                <p className="text-xs text-neutral-500 mb-2">Pin que identifica dónde está tu empresa (bodega/oficina). Solo es referencia visual.</p>
                <MapPicker
                  value={supplyValue}
                  onChange={onSupplyPin}
                  radiusKm={0}
                  circleColor={c.primary_color || "#0A0A0A"}
                />
                {c.supply_lat && c.supply_lng && (
                  <p className="text-xs text-neutral-500 mt-2" data-testid="supply-coords">
                    Coordenadas: {c.supply_lat.toFixed(5)}, {c.supply_lng.toFixed(5)}
                  </p>
                )}
              </div>

              <div className="pt-4 border-t border-neutral-100">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label>Área de cobertura</Label>
                    <p className="text-xs text-neutral-500 mt-1">
                      Define una o varias zonas dibujando polígonos en el mapa. Los pedidos fuera de estas áreas se marcarán como fuera de servicio.
                    </p>
                  </div>
                </div>
                <ServiceAreaEditor
                  polygons={Array.isArray(c.service_area_polygons) ? c.service_area_polygons : []}
                  onChange={(next) => setC({ ...c, service_area_polygons: next })}
                  pin={c.supply_lat && c.supply_lng ? { lat: c.supply_lat, lng: c.supply_lng } : null}
                  color={c.primary_color || "#0A0A0A"}
                />
              </div>
            </div>
          </TabsContent>

          {/* PIPAS */}
          <TabsContent value="pipas" className="mt-4">
            <SamsaraConfigCard
              c={c}
              setC={setC}
              kind="pipas"
              title="Autotanques"
              helperLabel="autotanques"
            />
          </TabsContent>

          {/* CILINDRERAS */}
          <TabsContent value="cilindreras" className="mt-4">
            <SamsaraConfigCard
              c={c}
              setC={setC}
              kind="cilindreras"
              title="Cilindreras"
              helperLabel="cilindreras"
            />
          </TabsContent>

          {/* ESTACIONES */}
          <TabsContent value="estaciones" className="mt-4">
            <StationsManager />
          </TabsContent>

          {!SAVE_DISABLED[tab] && (() => {
            const ubicacionesInvalid =
              tab === "ubicaciones" &&
              (!Array.isArray(c.service_area_polygons) || c.service_area_polygons.length === 0);
            return (
              <Button
                onClick={save}
                disabled={ubicacionesInvalid}
                className="w-full h-12 mt-4 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="save-branding-btn"
              >
                {ubicacionesInvalid ? "Dibuja al menos un área de cobertura" : (SAVE_LABEL[tab] || "Guardar cambios")}
              </Button>
            );
          })()}
        </Tabs>
      </main>

      <ImageCropDialog
        open={cropOpen}
        onOpenChange={setCropOpen}
        imageSrc={cropImageSrc}
        onConfirm={confirmCroppedUpload}
        title="Ajusta el logo de tu empresa"
        description="Mueve, haz zoom y centra. Verás el resultado en círculo, como aparecerá para tus clientes."
      />
    </div>
  );
}

function SamsaraConfigCard({ c, setC, kind = "pipas", title = "Pipas", helperLabel = "vehículos" }) {
  const confirm = useConfirm();
  const [tags, setTags] = useState([]); // [{id, name, vehicles: [{id, name}]}]
  const [loadingTags, setLoadingTags] = useState(false);
  const [connectionValid, setConnectionValid] = useState(false);
  // `selectedIds` now holds VEHICLE IDs (not tag IDs). Always starts empty until
  // the user runs "Probar conexión" or "Editar selección".
  const [selectedIds, setSelectedIds] = useState([]);

  // Map field names based on kind: pipas → samsara_*, cilindreras → samsara_cil_*
  const F = kind === "cilindreras"
    ? { token: "samsara_cil_api_token", region: "samsara_cil_region", group: "samsara_cil_group_id" }
    : { token: "samsara_api_token",     region: "samsara_region",     group: "samsara_group_id" };

  const curToken = c[F.token] || "";
  const curRegion = c[F.region] || "us";
  const curGroup = c[F.group] || "";

  const invalidate = () => {
    setConnectionValid(false);
    setTags([]);
    setSelectedIds([]);
    setC((prev) => ({ ...prev, [F.group]: "" }));
  };

  const resetAll = async () => {
    const ok = await confirm({
      title: `¿Reiniciar configuración de ${title}?`,
      description: `Esto borrará el token de API, la región y los vehículos guardados para "${title}". No afecta las unidades ya sincronizadas ni la otra configuración.`,
      confirmText: "Sí, reiniciar",
      destructive: true,
    });
    if (!ok) return;
    setConnectionValid(false);
    setTags([]);
    setSelectedIds([]);
    setC((prev) => ({
      ...prev,
      [F.token]: "",
      [F.region]: "us",
      [F.group]: "",
    }));
    try {
      await api.patch(`/companies/${c.company_id}`, {
        [F.token]: "",
        [F.region]: "us",
        [F.group]: "",
      });
      // Also wipe drivers of this kind so "Unidades de Reparto" doesn't keep showing stale ones.
      try {
        await api.delete(`/drivers?kind=${kind}`);
      } catch (_e) { /* non-fatal */ }
      toast.success(`Configuración de ${title} reiniciada`);
    } catch (e) {
      toast.error("No se pudo guardar el reinicio. Pulsa 'Guardar cambios' para persistir.");
    }
  };

  const commitSelection = (ids) => {
    setSelectedIds(ids);
    setC((prev) => ({ ...prev, [F.group]: ids.join(",") }));
  };

  const fetchTags = async () => {
    const { data } = await api.post("/drivers/samsara/tags", {
      api_token: curToken,
      region: curRegion,
      kind,
    });
    return data.tags || [];
  };

  const testConnection = async () => {
    if (!curToken) return;
    setLoadingTags(true);
    try {
      const fresh = await fetchTags();
      setTags(fresh);
      setConnectionValid(true);
      setSelectedIds([]);
      setC((prev) => ({ ...prev, [F.group]: "" }));
      const totalVehicles = fresh.reduce((s, t) => s + (t.vehicles?.length || 0), 0);
      if (!fresh.length) toast.info("Conexión OK, pero tu cuenta Samsara no tiene tags");
      else toast.success(`Conexión OK. ${fresh.length} tags · ${totalVehicles} ${helperLabel} disponibles`);
    } catch (e) {
      setConnectionValid(false);
      setTags([]);
      const detail = e?.response?.data?.detail || "No se pudo conectar con Samsara";
      toast.error(typeof detail === "string" ? detail : "Error de conexión");
    } finally {
      setLoadingTags(false);
    }
  };

  const editSavedSelection = async () => {
    if (!curToken) {
      toast.error("Configura el token antes de editar");
      return;
    }
    setLoadingTags(true);
    try {
      const fresh = await fetchTags();
      setTags(fresh);
      setConnectionValid(true);
      const saved = (curGroup || "").split(",").map((s) => s.trim()).filter(Boolean);
      setSelectedIds(saved);
      toast.success("Selección cargada — ajusta y guarda");
    } catch (e) {
      const detail = e?.response?.data?.detail || "No se pudo cargar la selección";
      toast.error(typeof detail === "string" ? detail : "Error de conexión");
    } finally {
      setLoadingTags(false);
    }
  };

  const toggleVehicle = (id) => {
    const set = new Set(selectedIds);
    if (set.has(id)) set.delete(id); else set.add(id);
    commitSelection(Array.from(set));
  };

  const toggleTagVehicles = (tag, allSelected) => {
    const set = new Set(selectedIds);
    for (const v of tag.vehicles || []) {
      if (allSelected) set.delete(v.id); else set.add(v.id);
    }
    commitSelection(Array.from(set));
  };

  const hasSavedSelection = !!(curGroup || "").trim() && !!curToken;
  const hasSavedToken = !!curToken;

  // Three-state banner:
  //   - "Samsara conectado" (green)   → just validated in this session OR has saved config
  //   - (none in-between — removed to avoid the false "sin configurar" when token is saved)
  //   - "Sin configurar" (amber)      → no token saved and nothing tested
  const isConnected = connectionValid || hasSavedToken;

  const selectedCount = selectedIds.length;

  return (
    <div className="space-y-4">
      {/* Status banner */}
      <div className={`rounded-2xl border p-5 flex items-start gap-3 ${
        isConnected ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"
      }`}>
        <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 bg-white ${
          isConnected ? "text-green-600" : "text-amber-600"
        }`}>
          {isConnected ? <CheckCircle2 size={18} /> : <Satellite size={18} />}
        </div>
        <div className="flex-1">
          <h3 className="font-display font-bold text-lg">
            {isConnected ? `${title} — Samsara conectado` : `${title} — sin configurar`}
          </h3>
          <p className="text-sm text-neutral-600 mt-0.5">
            {connectionValid
              ? `El token es válido. Abre los tags abajo y marca las ${helperLabel} que quieres sincronizar.`
              : hasSavedToken
              ? `Configuración guardada. Pulsa "Probar conexión de API" o "Editar selección guardada" para trabajar con las ${helperLabel}.`
              : "Pega tu API Token de Samsara abajo y prueba la conexión."}
          </p>
        </div>
      </div>

      {/* Configuration form */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-6 space-y-5">
        <div>
          <h4 className="font-display font-bold">Credenciales Samsara — {title}</h4>
          <p className="text-xs text-neutral-500 mt-1">Las credenciales se obtienen en cloud.samsara.com → Settings → Public API → API Tokens.</p>
        </div>

        <div>
          <Label className="flex items-center gap-2">
            <KeyRound size={13} className="text-neutral-500" />API Access Token <span className="text-red-500">*</span>
          </Label>
          <div className="relative">
            <Input
              className={`h-11 font-mono text-sm ${connectionValid ? "border-green-500 pr-9" : ""}`}
              type="password"
              value={curToken}
              onChange={(e) => { setC({ ...c, [F.token]: e.target.value }); invalidate(); }}
              placeholder="samsara_api_xxxxxxxxxxxxxxxxxxxxx"
              data-testid={`samsara-token-input-${kind}`}
            />
            {connectionValid && (
              <CheckCircle2 size={16} className="text-green-600 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" data-testid={`samsara-token-valid-${kind}`} />
            )}
          </div>
        </div>

        <div>
          <Label className="flex items-center gap-2">
            <Globe size={13} className="text-neutral-500" />Región
          </Label>
          <div className="relative mt-1">
            <Select
              value={curRegion}
              onValueChange={(v) => { setC({ ...c, [F.region]: v }); invalidate(); }}
            >
              <SelectTrigger
                className={`h-11 ${connectionValid ? "border-green-500 pr-10" : ""}`}
                data-testid={`samsara-region-select-${kind}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="us">Estados Unidos (api.samsara.com)</SelectItem>
                <SelectItem value="eu">Europa (api.eu.samsara.com)</SelectItem>
              </SelectContent>
            </Select>
            {connectionValid && (
              <CheckCircle2 size={16} className="text-green-600 absolute right-9 top-1/2 -translate-y-1/2 pointer-events-none" data-testid={`samsara-region-valid-${kind}`} />
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-2">
          <Button
            variant="outline"
            onClick={testConnection}
            disabled={loadingTags || !curToken}
            className="h-11"
            data-testid={`gps-test-connection-btn-${kind}`}
          >
            <RefreshCw size={14} className={`mr-2 ${loadingTags ? "animate-spin" : ""}`} />
            {loadingTags && !connectionValid ? "Probando..." : "Probar conexión de API"}
          </Button>
          <Button
            variant="outline"
            onClick={editSavedSelection}
            disabled={loadingTags || !hasSavedSelection}
            className="h-11"
            data-testid={`gps-edit-selection-btn-${kind}`}
            title={!hasSavedSelection ? "No hay una selección guardada aún" : "Cargar la selección guardada para editarla"}
          >
            <Pencil size={14} className="mr-2" />
            Editar selección guardada
          </Button>
        </div>

        {/* Tag → vehicle multi-select (enabled only after a valid connection) */}
        <div className="pt-2 border-t border-neutral-100">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="flex items-center gap-2 mb-0">
                <Tag size={13} className="text-neutral-500" />Tags y {helperLabel} disponibles
              </Label>
              {connectionValid && (
                <span
                  className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 border ${
                    selectedCount === 0
                      ? "bg-amber-50 border-amber-200 text-amber-700"
                      : "bg-green-50 border-green-200 text-green-700"
                  }`}
                  data-testid={`samsara-vehicle-count-${kind}`}
                  title={`Unidades seleccionadas para ${title}`}
                >
                  {selectedCount} {selectedCount === 1 ? "unidad" : "unidades"}
                </span>
              )}
            </div>
            {connectionValid && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-xs"
                onClick={testConnection}
                disabled={loadingTags}
                data-testid={`samsara-refresh-tags-btn-${kind}`}
              >
                <RefreshCw size={12} className={`mr-1.5 ${loadingTags ? "animate-spin" : ""}`} />
                Recargar
              </Button>
            )}
          </div>

          {!connectionValid ? (
            <Input
              className="h-11 mt-1"
              value=""
              disabled
              placeholder="Prueba primero la conexión de API para habilitar"
              data-testid="samsara-group-input-disabled"
            />
          ) : tags.length === 0 ? (
            <p className="text-sm text-neutral-500 mt-2 px-3 py-4 rounded-md bg-neutral-50 border border-neutral-200">
              Tu cuenta Samsara no tiene tags configurados. Créalos en cloud.samsara.com primero.
            </p>
          ) : (
            <Accordion
              type="multiple"
              className="mt-2 rounded-md border border-input divide-y divide-neutral-100"
              data-testid="samsara-tag-accordion"
            >
              {tags.map((t) => {
                const vehs = t.vehicles || [];
                const inTag = vehs.filter((v) => selectedIds.includes(v.id)).length;
                const allSel = vehs.length > 0 && inTag === vehs.length;
                return (
                  <AccordionItem key={t.id} value={t.id} className="border-0" data-testid={`samsara-tag-item-${t.id}`}>
                    <AccordionTrigger className="px-3 py-2 hover:bg-neutral-50 hover:no-underline">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-sm font-medium truncate">{t.name}</span>
                        <span className="text-[10px] text-neutral-500 font-mono shrink-0">{vehs.length} veh.</span>
                        {inTag > 0 && (
                          <span className="ml-auto mr-2 text-[10px] font-semibold rounded-full bg-green-50 border border-green-200 text-green-700 px-2 py-0.5 shrink-0">
                            {inTag}/{vehs.length}
                          </span>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-3 pt-0">
                      {vehs.length === 0 ? (
                        <p className="text-xs text-neutral-400 pl-1 py-2">Sin vehículos en este tag.</p>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => toggleTagVehicles(t, allSel)}
                            className="text-[11px] font-semibold text-neutral-600 hover:text-black underline pl-1 mb-1"
                            data-testid={`samsara-tag-toggleall-${t.id}`}
                          >
                            {allSel ? "Desmarcar todos" : "Marcar todos"}
                          </button>
                          <div className="rounded-md border border-neutral-100 max-h-64 overflow-y-auto divide-y divide-neutral-50">
                            {vehs.map((v) => {
                              const checked = selectedIds.includes(v.id);
                              return (
                                <label
                                  key={v.id}
                                  className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-neutral-50 ${checked ? "bg-neutral-50" : ""}`}
                                  data-testid={`samsara-vehicle-option-${v.id}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleVehicle(v.id)}
                                    className="h-4 w-4 accent-[color:var(--company-primary)]"
                                  />
                                  <span className="text-sm truncate">{v.name}</span>
                                  <span className="ml-auto text-[10px] text-neutral-400 font-mono shrink-0">{v.id}</span>
                                </label>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}

          {connectionValid && selectedCount > 0 && (
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={() => commitSelection([])}
                className="text-xs text-neutral-500 hover:text-black underline"
                data-testid={`samsara-clear-selection-${kind}`}
              >
                Limpiar selección
              </button>
            </div>
          )}

          <p className="text-xs text-neutral-400 mt-1.5">
            {connectionValid
              ? `Abre cada tag y marca las ${helperLabel} que quieres sincronizar. Se ordenan por nombre.`
              : `Las ${helperLabel} se habilitan después de validar la conexión.`}
          </p>
        </div>

        <div className="pt-3 border-t border-neutral-100 flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-neutral-400">
            Recuerda guardar los cambios después de elegir las {helperLabel}.
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={resetAll}
            className="h-9 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid={`samsara-reset-btn-${kind}`}
          >
            <RotateCcw size={13} className="mr-1.5" />
            Reiniciar configuración {title}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================== Stations Manager ==============================

const EMPTY_STATION = { name: "", phone: "", whatsapp: "", full_address: "", lat: null, lng: null };

function StationsManager() {
  const confirm = useConfirm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_STATION);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stations");
      setList(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_STATION);
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditingId(s.station_id);
    setForm({
      name: s.name || "",
      phone: s.phone || "",
      whatsapp: s.whatsapp || "",
      full_address: s.full_address || "",
      lat: s.lat ?? null,
      lng: s.lng ?? null,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("El nombre es requerido"); return; }
    if (form.lat == null || form.lng == null) { toast.error("Selecciona una ubicación en el mapa"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone || null,
        whatsapp: form.whatsapp || null,
        full_address: form.full_address || null,
        lat: Number(form.lat),
        lng: Number(form.lng),
      };
      if (editingId) {
        await api.patch(`/stations/${editingId}`, payload);
        toast.success("Estación actualizada");
      } else {
        await api.post("/stations", payload);
        toast.success("Estación agregada");
      }
      setDialogOpen(false);
      setForm(EMPTY_STATION);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    const ok = await confirm({
      title: "¿Eliminar estación?",
      description: `Se eliminará "${s.name}". Esta acción no se puede deshacer.`,
      confirmText: "Sí, eliminar",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/stations/${s.station_id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-xl font-extrabold tracking-tight">Estaciones</h3>
          <p className="text-sm text-neutral-500 mt-0.5">
            Tus puntos de distribución físicos. Cada estación tiene una ubicación en el mapa.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="h-11 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90"
          data-testid="add-station-btn"
        >
          <Plus size={16} className="mr-2" />Agregar estación
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-neutral-500">Cargando estaciones…</p>
      ) : list.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-neutral-300 p-8 text-center">
          <Fuel size={32} className="mx-auto text-neutral-400 mb-2" />
          <p className="text-sm text-neutral-500">Aún no tienes estaciones. Agrega la primera para que aparezca en el listado.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {list.map((s) => (
            <div
              key={s.station_id}
              className="bg-white border border-neutral-200 rounded-2xl p-4 flex gap-3"
              data-testid={`station-item-${s.station_id}`}
            >
              <div className="h-10 w-10 rounded-full bg-company/10 text-[color:var(--company-primary)] flex items-center justify-center shrink-0">
                <Fuel size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{s.name}</p>
                {s.full_address && (
                  <p className="text-xs text-neutral-500 mt-0.5 flex items-start gap-1">
                    <MapPin size={11} className="mt-0.5 shrink-0" />
                    <span className="line-clamp-2">{s.full_address}</span>
                  </p>
                )}
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-neutral-600">
                  {s.phone && <span className="inline-flex items-center gap-1"><Phone size={11} />{s.phone}</span>}
                  {s.whatsapp && <span className="inline-flex items-center gap-1 text-green-700"><MessageCircle size={11} />{s.whatsapp}</span>}
                </div>
                <div className="flex gap-1 mt-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(s)} data-testid={`edit-station-${s.station_id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => remove(s)} data-testid={`delete-station-${s.station_id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <BottomSheet
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={editingId ? "Editar estación" : "Nueva estación"}
        description="Datos básicos y ubicación de la estación."
        testid="station-form-sheet"
      >
        <div className="px-4 sm:px-6 pb-4 sm:pb-6 overflow-y-auto max-h-[70vh]">
          <div className="space-y-3">
            <div>
              <Label>Nombre <span className="text-red-500">*</span></Label>
              <Input
                className="h-11 mt-1"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: Estación Norte"
                data-testid="station-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="flex items-center gap-1.5"><Phone size={12} />Teléfono</Label>
                <Input
                  className="h-11 mt-1"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+57 300 000 0000"
                  data-testid="station-phone-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1.5"><MessageCircle size={12} />WhatsApp</Label>
                <Input
                  className="h-11 mt-1"
                  value={form.whatsapp}
                  onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                  placeholder="+57 300 000 0000"
                  data-testid="station-whatsapp-input"
                />
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><MapPin size={12} />Ubicación en el mapa <span className="text-red-500">*</span></Label>
              <div className="mt-1 rounded-xl overflow-hidden border border-neutral-200" data-testid="station-map-picker">
                <MapPicker
                  value={{
                    lat: form.lat ?? undefined,
                    lng: form.lng ?? undefined,
                    full_address: form.full_address,
                  }}
                  onChange={(v) => setForm((prev) => ({
                    ...prev,
                    lat: v.lat,
                    lng: v.lng,
                    full_address: v.full_address ?? prev.full_address,
                  }))}
                />
              </div>
              {form.lat != null && form.lng != null && (
                <p className="text-xs text-neutral-500 mt-1 font-mono">
                  {Number(form.lat).toFixed(5)}, {Number(form.lng).toFixed(5)}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2 mt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 h-11">
              Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="flex-1 h-11 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90"
              data-testid="save-station-btn"
            >
              {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Agregar estación"}
            </Button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}

/**
 * Métodos de pago configurables por la empresa. Cada checkbox controla
 * si un método aparece para el cliente en el wizard. La lista canónica
 * vive en `lib/paymentMethods.js` para mantener una sola fuente de
 * verdad entre admin y cliente.
 *
 * Tratamos `selected==null` como "usar default ([cash, stripe])" para
 * compatibilidad con empresas creadas antes de esta feature.
 */
function PaymentMethodsConfig({ selected, onChange }) {
  const effective = Array.isArray(selected) && selected.length > 0
    ? selected
    : DEFAULT_ENABLED_METHODS;

  const toggle = (id) => {
    const next = effective.includes(id)
      ? effective.filter((x) => x !== id)
      : [...effective, id];
    onChange(next);
  };

  return (
    <div className="rounded-xl border border-neutral-200 p-4 space-y-3" data-testid="payment-methods-config">
      <div>
        <Label className="text-sm font-semibold">Métodos de pago disponibles</Label>
        <p className="text-xs text-neutral-500 mt-0.5">
          Los clientes sólo verán estos métodos en el wizard de pedido. Puedes activar varios bancos para transferencias y combinarlos con efectivo o pago en línea.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {PAYMENT_METHODS.map((m) => {
          const active = effective.includes(m.id);
          return (
            <label
              key={m.id}
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                active
                  ? "border-company bg-[color:var(--company-primary)]/5"
                  : "border-neutral-200 hover:border-neutral-300"
              }`}
              data-testid={`pm-toggle-${m.id}`}
            >
              <input
                type="checkbox"
                checked={active}
                onChange={() => toggle(m.id)}
                className="h-4 w-4 accent-[color:var(--company-primary)]"
                data-testid={`pm-checkbox-${m.id}`}
              />
              <div className="h-11 w-[80px] rounded-lg bg-white border border-neutral-200 flex items-center justify-center shrink-0 p-1">
                {m.id === "cash" ? (
                  <Banknote size={28} className="text-emerald-600" />
                ) : (
                  <PaymentLogo id={m.id} size={28} maxWidth={72} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm truncate">{m.label}</p>
                <p className="text-[11px] text-neutral-500 truncate">{m.description}</p>
              </div>
            </label>
          );
        })}
      </div>
      {effective.length === 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
          ⚠ Sin métodos seleccionados, los clientes no podrán completar pedidos.
        </p>
      )}
    </div>
  );
}
